import { ChangeEvent, useMemo } from 'react';

import { Story } from 'services/story.service';
import { Folder } from 'services/folders.service';
import { Payment } from 'services/payment.service';
import { FinalCut, RenderStatus } from 'services/finalcut.service';
import { Project, ProjectStatus } from 'services/project.service';
import { FileStatus, FileType, ProjectFile } from 'services/file.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { getPath, Path } from 'components/_hooks/use-router';
import { ProjectTableSearchKey, setTableSearchFilter } from 'components/app/projects/_mediator/projects.slice';
import { prettyBytes, prettyDate, prettyDuration, prettyFrameRate } from '../_helpers/_app.helper';
import { mapFileStatusToLabel } from './_mediator/projects.helper';

export const useProjectsSearch = (key: ProjectTableSearchKey) => {
  const { storiesSearchFilter, projectsSearchFilter, filesSearchFilter, finalCutsSearchFilter, foldersSearchFilter } =
    useAppSelector((state) => state.projects);
  const dispatch = useAppDispatch();

  const searchValue = useMemo(
    () =>
      ({ foldersSearchFilter, storiesSearchFilter, projectsSearchFilter, filesSearchFilter, finalCutsSearchFilter })[
        key
      ] ?? '',
    [foldersSearchFilter, storiesSearchFilter, projectsSearchFilter, filesSearchFilter, finalCutsSearchFilter]
  );

  const updateSearchValue = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(setTableSearchFilter({ key: key, value: e.target.value }));
  };

  const getFilteredList = <T extends Folder | Story | Project | ProjectFile | FinalCut>(entities: T[]) => {
    const getSearchResult = (value: string) => value?.toLowerCase().includes(searchValue?.toLowerCase()) ?? false;
    return entities.filter((entity) => {
      return Object.keys(entity).some((key) => {
        const value = entity[key as keyof T];
        switch (key) {
          case 'name':
            return getSearchResult(value as string);
          case 'createdAt':
          case 'accessedAt':
          case 'updatedAt':
          case 'mediaOriginDate':
            return getSearchResult(prettyDate(value as string));
          case 'size':
            return getSearchResult(prettyBytes(value as number));
          case 'fileName':
            return getSearchResult(value as string);
          case 'fileType':
            return getSearchResult(FileType[value as FileType]);
          case 'duration':
            return getSearchResult(prettyDuration(value as number));
          case 'status':
            if ((entities[0] as Project).projectId !== undefined)
              return getSearchResult(ProjectStatus[value as ProjectStatus]);
            if ((entities[0] as ProjectFile).fileWithEffectsUrls !== undefined)
              return getSearchResult(mapFileStatusToLabel(value as FileStatus));
            if ((entities[0] as FinalCut).frameRate !== undefined)
              return getSearchResult(RenderStatus[value as RenderStatus]);
            return true;
          case 'typeName': {
            if ((entities[0] as FinalCut).payment?.price.name !== undefined) return getSearchResult(value as string);
            return true;
          }
          case 'frameRate': {
            return getSearchResult(prettyFrameRate(value as number) as string);
          }
          case 'payment':
          case 'subscription': {
            return getSearchResult((value as Payment)?.price.name as string);
          }
        }
      });
    });
  };

  const clearSearchValue = (entity?: string | string[]) => {
    if (getPath() !== Path.projects) return;

    const lowerTrim = (value: string) => value.toLowerCase().trim();

    if (typeof entity === 'string') {
      if (lowerTrim(entity).includes(lowerTrim(searchValue))) return;
    }

    if (Array.isArray(entity)) {
      if (entity.some((item) => lowerTrim(item).includes(lowerTrim(searchValue)))) return;
    }

    dispatch(setTableSearchFilter({ key: key, value: null }));
  };

  return { searchValue, updateSearchValue, getFilteredList, clearSearchValue };
};
